import {
  FocusEvent,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  getCountryData,
  getCountryDataList,
  getEmojiFlag,
  ICountry,
  ICountryData,
  TCountryCode,
} from 'countries-list';

import { ConnectedFields } from '@components/primitives/ConnectedFields';
import { useFormField } from '@components/primitives/FormField';
import { Input, InputProps } from '@components/primitives/Input';
import { Select } from '@components/primitives/Select';
import { cn } from '@components/primitives/utilities';

import css from './styles.module.css';

function getCountryFromCode(code: string): ICountryData | null {
  const data = getCountryData(code as TCountryCode);
  if (!data.name) return null;
  return data;
}

/** Normalises the dial code to remove the + sign */
function getDialCode(country: ICountry) {
  return country.phone[0].toString();
}

/** All valid countries, with the US and GB first */
const allCountries = getCountryDataList().sort((a) => {
  if (a.iso2 === 'US') return -1;
  if (a.iso2 === 'GB') return -1;
  return 0;
});

interface EmojiIconProps {
  emoji: string;
}
function EmojiIcon({ emoji }: EmojiIconProps) {
  return (
    <span className={css.emojiIcon} aria-hidden>
      {emoji}
    </span>
  );
}

/** Gets the country from the value, using fallback if not found */
function getCountryFromValue(
  value: InputProps['value'],
  fallbackCountry: TCountryCode
) {
  if (!value) return fallbackCountry;

  const dialCode = String(value).match(/^\+(\d+)\s*/)?.[1];
  if (!dialCode) {
    return fallbackCountry;
  }

  const fallback = getCountryFromCode(fallbackCountry);
  if (fallback && getDialCode(fallback) === dialCode) {
    return fallback.iso2;
  }

  const country = allCountries.find((c) => getDialCode(c) === dialCode);
  if (!country) {
    return fallbackCountry;
  }

  return country.iso2;
}

/** Gets the phone number from the value, removing the dial code */
function getTelFromValue(value: InputProps['value']) {
  if (!value) return '';
  return String(value).replace(/\+\d+\s+/g, '');
}

type TelInputProps = Omit<InputProps, 'value' | 'onChange' | 'type'> & {
  value: string;
  onChange: (value: string) => void;
};

export const TelInput = forwardRef<HTMLInputElement, TelInputProps>(
  function TelInput({ value, onChange, size, error, ...props }, ref) {
    const fieldsRef = useRef<HTMLDivElement>(null);

    const formField = useFormField();

    const [country, setCountry] = useState(getCountryFromValue(value, 'US'));
    const dialCode = useMemo(() => {
      const countryMatch = getCountryFromCode(country);
      if (!countryMatch) return '1'; // Default to US if no country found
      return getDialCode(countryMatch);
    }, [country]);

    const [tel, setTel] = useState(getTelFromValue(value));

    // Update the internal state when `value={...}` changes
    useEffect(() => {
      setCountry(getCountryFromValue(value, country));
      setTel(getTelFromValue(value));
    }, [value]);

    // Update via `onChange` when internal state changes
    const handleChange = useRef(onChange);
    handleChange.current = onChange;
    useEffect(() => {
      handleChange.current?.(`+${dialCode} ${tel}`);
    }, [dialCode, tel]);

    const handleBlur = useCallback(
      (event: FocusEvent<any>) => {
        const fields = fieldsRef.current;
        if (!fields) {
          props?.onBlur?.(event);
          return;
        }

        requestAnimationFrame(() => {
          const hasFocus = fields.contains(document.activeElement);
          if (!hasFocus) {
            props?.onBlur?.(event);
          }
        });
      },
      [props?.onBlur]
    );

    return (
      <ConnectedFields ref={fieldsRef} className={css.telFields}>
        <Select
          className={css.countrySelect}
          value={country}
          onChange={setCountry}
          align="start"
          size={size}
          error={error ?? Boolean(formField?.error)}
          onBlur={handleBlur}
        >
          {allCountries.map((country) => (
            <Select.Option
              key={country.iso2}
              value={country.iso2}
              textValue={getDialCode(country)}
              icon={<EmojiIcon emoji={getEmojiFlag(country.iso2)} />}
            >
              <span className={css.countryName}>{country.name}</span>
              <span className={css.dialCode}>+{getDialCode(country)}</span>
            </Select.Option>
          ))}
        </Select>

        <Input
          {...props}
          ref={ref}
          className={cn(css.telInput, props.className)}
          value={tel}
          onChange={(e) => setTel(e.target.value)}
          onBlur={handleBlur}
        />
      </ConnectedFields>
    );
  }
);
